import React, { useState, useEffect, useRef } from 'react';
import './Dialog.css';

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z"/>
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

const QuestionIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
  </svg>
);

const ICONS = { info: InfoIcon, success: SuccessIcon, warning: WarningIcon, error: ErrorIcon, confirm: QuestionIcon };

const TYPE_COLORS = {
  info: '#007bff',
  success: '#28a745',
  warning: '#f0a500',
  error: '#dc3545',
  confirm: '#e67e22',
};

const DEFAULT_TITLES = {
  info: 'Información',
  success: '¡Listo!',
  warning: 'Atención',
  error: 'Error',
  confirm: 'Confirmar',
};

export default function Dialog({ dialog, onClose }) {
  const { kind, message, title, type, confirmLabel, cancelLabel, defaultValue, placeholder } = dialog;
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const inputRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const resolvedType = type || (kind === 'confirm' ? 'confirm' : 'info');
  const color = TYPE_COLORS[resolvedType] || TYPE_COLORS.info;
  const IconComponent = ICONS[resolvedType] || ICONS.info;
  const resolvedTitle = title || DEFAULT_TITLES[resolvedType] || 'Información';

  useEffect(() => {
    if (kind === 'prompt' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else if (confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [kind]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (kind === 'alert') onClose(undefined);
      else onClose(kind === 'prompt' ? null : false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (kind === 'alert') onClose(undefined);
      else onClose(kind === 'prompt' ? null : false);
    }
  };

  const handleConfirm = () => {
    if (kind === 'alert') onClose(undefined);
    else if (kind === 'confirm') onClose(true);
    else onClose(inputValue);
  };

  const handleCancel = () => {
    onClose(kind === 'prompt' ? null : false);
  };

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') handleCancel();
  };

  const isDanger = resolvedType === 'error' || resolvedType === 'confirm';

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="dialog-card" role="dialog" aria-modal="true">
        <div className="dialog-header" style={{ borderColor: color }}>
          <span className="dialog-icon" style={{ color }}>
            <IconComponent />
          </span>
          <h3 className="dialog-title">{resolvedTitle}</h3>
        </div>

        <div className="dialog-body">
          <p className="dialog-message">{message}</p>
          {kind === 'prompt' && (
            <input
              ref={inputRef}
              className="dialog-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder || ''}
              onKeyDown={handlePromptKeyDown}
            />
          )}
        </div>

        <div className="dialog-footer">
          {kind !== 'alert' && (
            <button className="dialog-btn dialog-btn-cancel" onClick={handleCancel}>
              {cancelLabel || 'Cancelar'}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            className={`dialog-btn dialog-btn-confirm ${isDanger ? 'dialog-btn-danger' : ''}`}
            style={!isDanger ? { backgroundColor: color, borderColor: color } : {}}
            onClick={handleConfirm}
          >
            {confirmLabel || (kind === 'alert' ? 'Aceptar' : kind === 'confirm' ? 'Confirmar' : 'Aceptar')}
          </button>
        </div>
      </div>
    </div>
  );
}
